'use client';

import { Target, Check, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useChildGoals, type Goal } from '@/hooks/use-goals';

interface ChildGoalsProps {
  childId: string;
}

const GOAL_CATEGORIES = [
  { value: 'technical', label: 'Technical', color: 'bg-blue-100 text-blue-700' },
  { value: 'tactical', label: 'Tactical', color: 'bg-purple-100 text-purple-700' },
  { value: 'physical', label: 'Physical', color: 'bg-green-100 text-green-700' },
  { value: 'mental', label: 'Mental', color: 'bg-amber-100 text-amber-700' },
] as const;

export function ChildGoals({ childId }: ChildGoalsProps) {
  const { data, isLoading } = useChildGoals(childId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeGoals = data?.goals.filter(g => g.status === 'active') ?? [];
  const completedGoals = data?.goals.filter(g => g.status === 'completed') ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Goals
          </CardTitle>
          {data?.stats && (
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-primary/10">
                {data.stats.active} Active
              </Badge>
              <Badge variant="outline" className="bg-primary/10">
                {data.stats.completed} Completed
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {activeGoals.length === 0 && completedGoals.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No goals set yet.</p>
            <p className="text-sm">The coach will set goals as training progresses.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Active Goals */}
            {activeGoals.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">Active Goals</h4>
                {activeGoals.map(goal => (
                  <GoalCard key={goal.id} goal={goal} />
                ))}
              </div>
            )}

            {/* Completed Goals */}
            {completedGoals.length > 0 && (
              <div className="space-y-3 mt-4">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Recently Completed
                </h4>
                {completedGoals.slice(0, 3).map(goal => (
                  <CompletedGoalCard key={goal.id} goal={goal} />
                ))}
                {completedGoals.length > 3 && (
                  <p className="text-xs text-muted-foreground text-center">
                    +{completedGoals.length - 3} more completed goals
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GoalCard({ goal }: { goal: Goal }) {
  const category = GOAL_CATEGORIES.find(c => c.value === goal.category);

  return (
    <div className="p-3 border border-border rounded-lg bg-card">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className={cn('text-xs', category?.color)}>
              {category?.label}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {goal.type === 'short_term' ? 'Short-term' : 'Long-term'}
            </Badge>
          </div>
          <h4 className="font-medium text-foreground">{goal.title}</h4>
          {goal.description && (
            <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>
          )}
          {goal.targetDate && (
            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              Target: {new Date(goal.targetDate).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CompletedGoalCard({ goal }: { goal: Goal }) {
  const category = GOAL_CATEGORIES.find(c => c.value === goal.category);

  return (
    <div className="p-3 border border-border rounded-lg bg-muted/50">
      <div className="flex items-start gap-2">
        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={cn('text-xs', category?.color)} variant="outline">
              {category?.label}
            </Badge>
            <span className="text-xs text-muted-foreground">
              Completed {goal.completedAt && new Date(goal.completedAt).toLocaleDateString()}
            </span>
          </div>
          <p className="text-sm text-foreground">{goal.title}</p>
          {goal.completionNotes && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              &ldquo;{goal.completionNotes}&rdquo;
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
